<template>
  <div>
    <el-card>
      <div slot="header">新增常见问题（多语言）</div>
      <p class="hint">至少填写一种语言的标题与答案；访客 H5 按当前界面语言展示，缺译时回退到简体中文或英语。</p>
      <el-form :model="form" label-width="72px">
        <el-form-item label="排序">
          <el-input-number v-model="form.sort_order" :min="0" />
        </el-form-item>
        <el-tabs v-model="createTab" type="border-card" class="lang-tabs">
          <el-tab-pane v-for="item in langOptions" :key="'c-' + item.code" :label="item.label" :name="item.code">
            <el-form-item label="标题">
              <el-input v-model="form.i18n[item.code].title" placeholder="该语言下的标题" />
            </el-form-item>
            <el-form-item label="答案">
              <el-input v-model="form.i18n[item.code].content" type="textarea" :rows="4" />
            </el-form-item>
          </el-tab-pane>
        </el-tabs>
        <el-form-item>
          <el-button type="primary" @click="submit">新增</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card style="margin-top:16px;">
      <div slot="header" class="card-head-import">
        <span>常见问题列表</span>
        <div class="import-tools">
          <span class="hint">CSV：title,content[,sort_order][,lang]</span>
          <input ref="csvInput" type="file" accept=".csv,text/csv" class="hidden-file" @change="onCsv" />
          <el-button size="small" @click="$refs.csvInput.click()">导入 CSV</el-button>
        </div>
      </div>
      <el-table :data="list">
        <el-table-column prop="id" label="ID" width="72" />
        <el-table-column prop="sort_order" label="排序" width="72" />
        <el-table-column label="标题（主语言预览）" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ row.title }}</template>
        </el-table-column>
        <el-table-column label="已配置语言" width="200">
          <template slot-scope="{ row }">
            <el-tag v-for="code in langCodesOf(row)" :key="row.id + '-' + code" size="mini" style="margin:2px;">
              {{ langLabel(code) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openEdit(row)">多语言编辑</el-button>
            <el-button size="mini" type="danger" @click="remove(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog title="编辑常见问题（多语言）" :visible.sync="editVisible" width="640px" @closed="editRow = null">
      <el-form v-if="editRow" label-width="72px">
        <el-form-item label="排序">
          <el-input-number v-model="editRow.sort_order" :min="0" />
        </el-form-item>
        <el-tabs v-model="editTab" type="border-card" class="lang-tabs">
          <el-tab-pane v-for="item in langOptions" :key="'e-' + item.code" :label="item.label" :name="item.code">
            <el-form-item label="标题">
              <el-input v-model="editRow.i18n[item.code].title" />
            </el-form-item>
            <el-form-item label="答案">
              <el-input v-model="editRow.i18n[item.code].content" type="textarea" :rows="5" />
            </el-form-item>
          </el-tab-pane>
        </el-tabs>
      </el-form>
      <span slot="footer">
        <el-button @click="editVisible = false">取消</el-button>
        <el-button type="primary" @click="saveEdit">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from '../api/request';
import { faqList, createFaq, updateFaq, deleteFaq } from '../api/merchant';

const LANG_OPTIONS = [
  { code: 'zh-CN', label: '简体中文' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'zh-TW', label: '繁體中文' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'pt', label: 'Português' },
  { code: 'es', label: 'Español' },
  { code: 'id', label: 'Bahasa Indonesia' }
];

function emptyI18n() {
  const i18n = {};
  LANG_OPTIONS.forEach(item => {
    i18n[item.code] = { title: '', content: '' };
  });
  return i18n;
}

function mergeI18n(raw) {
  const base = emptyI18n();
  if (!raw || typeof raw !== 'object') return base;
  Object.keys(raw).forEach(code => {
    if (!base[code]) return;
    base[code] = {
      title: (raw[code] && raw[code].title) || '',
      content: (raw[code] && raw[code].content) || ''
    };
  });
  return base;
}

function packI18n(i18n) {
  const out = {};
  Object.keys(i18n || {}).forEach(code => {
    const title = (i18n[code].title || '').trim();
    const content = (i18n[code].content || '').trim();
    if (title && content) out[code] = { title, content };
  });
  return out;
}

export default {
  data() {
    return {
      langOptions: LANG_OPTIONS,
      list: [],
      form: { sort_order: 0, i18n: emptyI18n() },
      createTab: 'zh-CN',
      editVisible: false,
      editRow: null,
      editTab: 'zh-CN'
    };
  },
  methods: {
    langLabel(code) {
      const hit = LANG_OPTIONS.find(x => x.code === code);
      return hit ? hit.label : code;
    },
    langCodesOf(row) {
      return Object.keys(packI18n(row.i18n || {}));
    },
    async load() {
      const { data } = await faqList();
      this.list = (data.data || []).map(row => ({
        ...row,
        i18n: mergeI18n(row.i18n)
      }));
    },
    async submit() {
      const i18n = packI18n(this.form.i18n);
      if (!Object.keys(i18n).length) {
        this.$message.warning('请至少填写一种语言的标题与答案');
        return;
      }
      await createFaq({ sort_order: this.form.sort_order, i18n });
      this.form = { sort_order: 0, i18n: emptyI18n() };
      this.createTab = 'zh-CN';
      this.$message.success('已新增');
      await this.load();
    },
    openEdit(row) {
      this.editRow = {
        id: row.id,
        sort_order: row.sort_order || 0,
        i18n: mergeI18n(row.i18n)
      };
      this.editTab = 'zh-CN';
      this.editVisible = true;
    },
    async saveEdit() {
      if (!this.editRow) return;
      const i18n = packI18n(this.editRow.i18n);
      if (!Object.keys(i18n).length) {
        this.$message.warning('请至少保留一种语言的标题与答案');
        return;
      }
      await updateFaq(this.editRow.id, {
        sort_order: this.editRow.sort_order,
        i18n
      });
      this.editVisible = false;
      this.$message.success('已保存');
      await this.load();
    },
    async remove(id) {
      await deleteFaq(id);
      await this.load();
    },
    async onCsv(e) {
      const input = e.target;
      const file = input.files && input.files[0];
      input.value = '';
      if (!file) return;
      const fd = new FormData();
      fd.append('file', file);
      try {
        const { data } = await request.post('/merchant/faq/import', fd);
        const r = data.data || {};
        this.$message.success(`导入完成：新增 ${r.created || 0} 条，跳过 ${r.skipped || 0} 行`);
        await this.load();
      } catch (err) {
        this.$message.error((err.response && err.response.data && err.response.data.message) || '导入失败');
      }
    }
  },
  created() {
    this.load();
  }
};
</script>

<style scoped>
.hint {
  font-size: 12px;
  color: #909399;
  margin: 0 0 12px;
}
.lang-tabs {
  margin-bottom: 12px;
}
.card-head-import {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.import-tools {
  display: flex;
  align-items: center;
  gap: 12px;
}
.hidden-file {
  display: none;
}
</style>
